import { supabase } from '../supabase';
import {
  Report,
  ReportView,
  ReportShare,
  ReportAnalytics,
  CreateReportPayload,
  UpdateReportPayload,
  ReportStatus,
  ShareMethod,
} from '../utils/types';

/**
 * Generate a URL-friendly slug from a title
 */
export const generateSlug = (title: string): string => {
  return (
    title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/--+/g, '-') + // Replace multiple hyphens with single
    '-' +
    Date.now()
  ); // Add timestamp to ensure uniqueness
};

/**
 * Get all reports with optional filters
 */
export const getReports = async (filters?: {
  status?: ReportStatus;
  visibility?: 'open' | 'private';
  search?: string;
}): Promise<Report[]> => {
  try {
    let query = supabase
      .from('reports')
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.visibility) {
      query = query.eq('visibility', filters.visibility);
    }

    if (filters?.search) {
      query = query.ilike('title', `%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data as Report[];
  } catch (error) {
    console.error('Error fetching reports:', error);
    return [];
  }
};

/**
 * Get a single report by ID (for editing)
 */
export const getReportById = async (id: number): Promise<Report | null> => {
  try {
    const { data, error } = await supabase
      .from('reports')
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .eq('id', id)
      .single();

    if (error) throw error;

    return data as Report;
  } catch (error) {
    console.error('Error fetching report:', error);
    return null;
  }
};

/**
 * Get a published report by slug (for public viewing)
 */
export const getReportBySlug = async (slug: string): Promise<Report | null> => {
  try {
    const { data, error } = await supabase
      .from('reports')
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .eq('slug', slug)
      .eq('status', 'published')
      .single();

    if (error) throw error;

    return data as Report;
  } catch (error) {
    console.error('Error fetching report by slug:', error);
    return null;
  }
};

/**
 * Create a new report
 */
export const createReport = async (
  payload: CreateReportPayload,
  userId: number
): Promise<Report | null> => {
  try {
    const slug = generateSlug(payload.title);

    const { data, error } = await supabase
      .from('reports')
      .insert({
        ...payload,
        slug,
        created_by: userId,
        status: 'draft',
      })
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .single();

    if (error) throw error;

    return data as Report;
  } catch (error) {
    console.error('Error creating report:', error);
    return null;
  }
};

/**
 * Update an existing report
 */
export const updateReport = async (
  id: number,
  payload: UpdateReportPayload
): Promise<Report | null> => {
  try {
    const { data, error } = await supabase
      .from('reports')
      .update(payload)
      .eq('id', id)
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .single();

    if (error) throw error;

    return data as Report;
  } catch (error) {
    console.error('Error updating report:', error);
    return null;
  }
};

/**
 * Publish a report
 */
export const publishReport = async (id: number): Promise<Report | null> => {
  try {
    const { data, error } = await supabase
      .from('reports')
      .update({
        status: 'published',
        published_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .single();

    if (error) throw error;

    return data as Report;
  } catch (error) {
    console.error('Error publishing report:', error);
    return null;
  }
};

/**
 * Archive a report
 */
export const archiveReport = async (id: number): Promise<Report | null> => {
  try {
    const { data, error } = await supabase
      .from('reports')
      .update({ status: 'archived' })
      .eq('id', id)
      .select(
        `
        *,
        author:users!reports_created_by_fkey(id, first_name, last_name, email)
      `
      )
      .single();

    if (error) throw error;

    return data as Report;
  } catch (error) {
    console.error('Error archiving report:', error);
    return null;
  }
};

/**
 * Delete a report
 */
export const deleteReport = async (id: number): Promise<boolean> => {
  try {
    const { error } = await supabase.from('reports').delete().eq('id', id);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting report:', error);
    return false;
  }
};

/**
 * Track a view of a report
 */
export const trackReportView = async (
  reportId: number,
  userId?: number | null,
  ipAddress?: string | null,
  userAgent?: string | null
): Promise<void> => {
  try {
    const { error } = await supabase.from('report_views').insert({
      report_id: reportId,
      user_id: userId || null,
      ip_address: ipAddress || null,
      user_agent: userAgent || null,
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error tracking report view:', error);
  }
};

/**
 * Track a share action
 */
export const trackReportShare = async (
  reportId: number,
  userId?: number | null,
  method?: ShareMethod
): Promise<void> => {
  try {
    const { error } = await supabase.from('report_shares').insert({
      report_id: reportId,
      shared_by: userId || null,
      share_method: method || 'link',
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error tracking report share:', error);
  }
};

/**
 * Get applause count for a report
 */
export const getReportApplauseCount = async (
  reportId: number
): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('report_applause')
      .select('*', { count: 'exact', head: true })
      .eq('report_id', reportId);

    if (error) throw error;
    return count || 0;
  } catch (error) {
    console.error('Error fetching applause count:', error);
    return 0;
  }
};

/**
 * Register an applause (clap) for a report
 */
export const addReportApplause = async (
  reportId: number,
  userId?: number | null
): Promise<{ ok: boolean }> => {
  try {
    const { error } = await supabase.from('report_applause').insert({
      report_id: reportId,
      user_id: userId || null,
    });

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('Error adding applause:', error);
    return { ok: false };
  }
};

/**
 * Get analytics for a specific report
 */
export const getReportAnalytics = async (
  reportId: number
): Promise<ReportAnalytics | null> => {
  try {
    // Get total and unique views
    const { data: viewsData, error: viewsError } = await supabase
      .from('report_views')
      .select('id, user_id, viewed_at')
      .eq('report_id', reportId);

    if (viewsError) throw viewsError;

    const totalViews = viewsData?.length || 0;
    const uniqueUserIds = new Set(
      viewsData?.filter((v) => v.user_id).map((v) => v.user_id)
    );
    const uniqueViews = uniqueUserIds.size;

    // Get views over time (grouped by date)
    const viewsByDate = viewsData?.reduce(
      (acc, view) => {
        const date = new Date(view.viewed_at).toISOString().split('T')[0];
        const existing = acc.find((item) => item.date === date);
        if (existing) {
          existing.count++;
        } else {
          acc.push({ date, count: 1 });
        }
        return acc;
      },
      [] as { date: string; count: number }[]
    );

    // Get views by user
    const { data: viewsByUserData, error: viewsByUserError } = await supabase
      .from('report_views')
      .select(
        `
        user_id,
        users!report_views_user_id_fkey(first_name, last_name)
      `
      )
      .eq('report_id', reportId)
      .not('user_id', 'is', null);

    if (viewsByUserError) throw viewsByUserError;

    const viewsByUser = Object.entries(
      viewsByUserData?.reduce((acc: any, view: any) => {
        const userId = view.user_id;
        if (!acc[userId]) {
          acc[userId] = {
            user_id: userId,
            user_name: `${view.users?.first_name} ${view.users?.last_name}`,
            count: 0,
          };
        }
        acc[userId].count++;
        return acc;
      }, {}) || {}
    ).map(([_, value]) => value) as {
      user_id: number;
      user_name: string;
      count: number;
    }[];

    // Get views by exporter
    const { data: viewsByExporterData, error: viewsByExporterError } =
      await supabase
        .from('report_views')
        .select(
          `
          user_id,
          users!report_views_user_id_fkey(
            exporter_id,
            exporter:exporters(name)
          )
        `
        )
        .eq('report_id', reportId)
        .not('user_id', 'is', null)
        .not('users.exporter_id', 'is', null);

    if (viewsByExporterError) throw viewsByExporterError;

    const viewsByExporter = Object.entries(
      viewsByExporterData?.reduce((acc: any, view: any) => {
        const exporterId = view.users?.exporter_id;
        if (exporterId && !acc[exporterId]) {
          acc[exporterId] = {
            exporter_id: exporterId,
            exporter_name: view.users?.exporter?.name || 'Unknown',
            count: 0,
          };
        }
        if (exporterId) {
          acc[exporterId].count++;
        }
        return acc;
      }, {}) || {}
    ).map(([_, value]) => value) as {
      exporter_id: number;
      exporter_name: string;
      count: number;
    }[];

    // Get shares count and breakdown
    const { data: sharesData, error: sharesError } = await supabase
      .from('report_shares')
      .select('share_method')
      .eq('report_id', reportId);

    if (sharesError) throw sharesError;

    const sharesCount = sharesData?.length || 0;
    const sharesByMethod = Object.entries(
      sharesData?.reduce((acc: any, share) => {
        const method = share.share_method;
        acc[method] = (acc[method] || 0) + 1;
        return acc;
      }, {}) || {}
    ).map(([method, count]) => ({
      method: method as ShareMethod,
      count: count as number,
    }));

    return {
      total_views: totalViews,
      unique_views: uniqueViews,
      views_over_time: viewsByDate || [],
      views_by_user: viewsByUser,
      views_by_exporter: viewsByExporter,
      shares_count: sharesCount,
      shares_by_method: sharesByMethod,
    };
  } catch (error) {
    console.error('Error fetching report analytics:', error);
    return null;
  }
};

/**
 * Get reports with view and share counts
 */
export const getReportsWithStats = async (filters?: {
  status?: ReportStatus;
  visibility?: 'open' | 'private';
  search?: string;
}): Promise<Report[]> => {
  try {
    const reports = await getReports(filters);

    // Fetch view and share counts for each report
    const reportsWithStats = await Promise.all(
      reports.map(async (report) => {
        const { count: viewsCount } = await supabase
          .from('report_views')
          .select('*', { count: 'exact', head: true })
          .eq('report_id', report.id);

        const { count: sharesCount } = await supabase
          .from('report_shares')
          .select('*', { count: 'exact', head: true })
          .eq('report_id', report.id);

        const { count: applauseCount } = await supabase
          .from('report_applause')
          .select('*', { count: 'exact', head: true })
          .eq('report_id', report.id);

        return {
          ...report,
          views_count: viewsCount || 0,
          shares_count: sharesCount || 0,
          applause_count: applauseCount || 0,
        };
      })
    );

    return reportsWithStats;
  } catch (error) {
    console.error('Error fetching reports with stats:', error);
    return [];
  }
};

/**
 * Upload image to Supabase Storage for reports
 */
export const uploadReportImage = async (file: File): Promise<string | null> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('report-images')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('report-images')
      .getPublicUrl(filePath);

    return data.publicUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    return null;
  }
};

/**
 * Delete image from Supabase Storage
 */
export const deleteReportImage = async (url: string): Promise<boolean> => {
  try {
    // Extract file path from URL
    const urlParts = url.split('/report-images/');
    if (urlParts.length < 2) return false;

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from('report-images')
      .remove([filePath]);

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    return false;
  }
};
